/**
 * COL-621: a kickback may only be recorded, changed or removed while the
 * month's payout to the owner is still open.
 *
 * A kickback moves the rent the owner earned, and only a LIVE offset invoice
 * follows that: the payments trigger refreshes the month's unsent, unpaid
 * invoice (touch_offset_invoice_for_rent), whose fee — and the remainder it
 * opens when sent early — then carry the new figure. Once every offset invoice
 * of the month is sent or paid, nothing would ever pay the difference out, so
 * the write is refused (product decision, 2026-10-28: block closed months
 * rather than build a top-up).
 *
 * Checked right before the write, by both the tenant profile's rent editor and
 * the payouts popup, so a popup left open while the invoice went out cannot
 * slip one through on a stale view.
 */
import { supabase } from "@/integrations/supabase/client";

export type OffsetInvoiceState = {
  status: string | null;
  sent_at: string | null;
  metadata?: Record<string, unknown> | null;
  fee_basis?: Record<string, unknown> | null;
};

/**
 * Whether an invoice still follows collected rent — the TS twin of
 * offset_invoice_row_is_live (offset_invoice_live_fee migration) for rows the query below already
 * narrowed to offset, property-level: unsent, still open, a well-formed
 * fee_basis (every unit names its revenue model), and neither a
 * cancel-and-reissue replacement nor a hand-corrected fee.
 */
function followsRent(inv: OffsetInvoiceState): boolean {
  const meta = inv.metadata ?? {};
  const units = inv.fee_basis?.properties;
  return (
    (inv.status === "pending" || inv.status === "overdue") &&
    !inv.sent_at &&
    Array.isArray(units) &&
    units.every(
      (u) => !!u && typeof u === "object" && !!(u as any).revenue_model
    ) &&
    !("replaces_invoice_id" in meta) &&
    !("live_fee_overridden_at" in meta)
  );
}

/**
 * Closed = the month has offset invoices and none of them still follows the
 * rent. No invoice at all is open: the generator has not run yet and will bill
 * from the rent as it then stands.
 */
export function isOffsetMonthClosed(
  invoices: readonly OffsetInvoiceState[]
): boolean {
  if (invoices.length === 0) return false;
  return !invoices.some(followsRent);
}

/** Thrown when the month's payout is closed; nothing was written. */
export class RentKickbackClosedError extends Error {
  constructor() {
    super("rent-kickback-month-closed");
    this.name = "RentKickbackClosedError";
  }
}

/**
 * Throws RentKickbackClosedError when the payout for `dueDate`'s month on the
 * booking's unit is closed. The invoices are the ones the refresh trigger
 * would touch: offset, property-level (fee_basis.level = 'property'), for the
 * unit's billing owner (COALESCE(apartment lessor, property lessor),
 * CLAUDE.md) and that month.
 *
 * A preflight, not a lock: an invoice sent in the instant between this read
 * and the write is not caught. A database guard was not added because
 * apply_referral_reward writes the same column from a trigger, and refusing
 * it there would break the referral flow.
 */
export async function assertKickbackMonthOpen(
  bookingId: number | string,
  dueDate: string
): Promise<void> {
  const { data: booking, error: bookingError } = await supabase
    .from("bookings")
    .select(
      "id, rooms:room_id ( property_id, apartments:apartment_id ( lessor_id ), properties:property_id ( lessor_id ) )"
    )
    .eq("id", Number(bookingId))
    .maybeSingle();
  if (bookingError) throw bookingError;
  const room = (booking as any)?.rooms;
  const propertyId = room?.property_id ?? null;
  const lessorId =
    room?.apartments?.lessor_id ?? room?.properties?.lessor_id ?? null;
  // A unit with no billing owner has no offset invoice to close.
  if (propertyId == null || lessorId == null) return;

  const { data: invoices, error } = await supabase
    .from("management_invoices")
    .select("status, sent_at, metadata, fee_basis")
    .eq("settlement", "offset")
    .eq("property_id", propertyId)
    .eq("lessor_id", lessorId)
    .is("apartment_id", null)
    .eq("fee_basis->>level", "property")
    .eq("period_month", `${dueDate.slice(0, 7)}-01`)
    .neq("status", "canceled");
  if (error) throw error;
  if (isOffsetMonthClosed((invoices ?? []) as OffsetInvoiceState[]))
    throw new RentKickbackClosedError();
}
